/**
 * カードスタイル定義
 *
 * 新しいスタイルを追加する場合は、このファイルに定義を追加するだけで
 * 他のコードは自動的に対応します。
 *
 * @module cardStyles
 */

export const CARD_STYLES = {
  /**
   * スタンダードスタイル（旧：光沢）
   * ボーダーレス + グラデーション + 光沢エフェクト
   */
  glossy: {
    id: "glossy",
    label: "Standard",
    labelKey: "ui.cardStyleStandard",
    description: "現実のカードのような光沢感",
    className: `
      rounded-xl
      bg-gradient-to-br from-white via-white to-gray-50/30
      dark:from-slate-800 dark:via-slate-800 dark:to-slate-900/40
      shadow-[0_2px_8px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]
      dark:shadow-[0_2px_8px_rgba(0,0,0,0.4),0_1px_2px_rgba(0,0,0,0.3)]
      hover:shadow-[0_8px_24px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.08)]
      dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.5),0_2px_4px_rgba(0,0,0,0.4)]
      transition-shadow duration-300
      relative
      before:absolute before:inset-0
      before:bg-gradient-to-br before:from-white/40 before:via-transparent before:to-transparent
      dark:before:from-white/5
      before:opacity-60
    `
      .replace(/\s+/g, " ")
      .trim(),
  },

  /**
   * Flatスタイル
   * シンプルなボーダーのみ（角丸・シャドウなし）
   */
  standard: {
    id: "standard",
    label: "Flat",
    labelKey: "ui.cardStyleFlat",
    description: "シンプルなボーダースタイル",
    className: "ring-1 ring-inset ring-border bg-card dark:bg-slate-800/60",
  },

  // 🚀 将来の拡張例（コメントアウト）
  /*
  minimal: {
    id: "minimal",
    label: "ミニマル",
    description: "極限までシンプルに",
    className: "rounded bg-white hover:bg-gray-50 transition-colors duration-200",
  },

  glass: {
    id: "glass",
    label: "グラスモーフィズム",
    description: "半透明ガラス風",
    className: `
      rounded-2xl
      bg-white/70 backdrop-blur-xl
      border border-white/20
      shadow-[0_8px_32px_rgba(0,0,0,0.1)]
      hover:shadow-[0_12px_48px_rgba(0,0,0,0.15)]
      transition-all duration-300
    `.replace(/\s+/g, ' ').trim(),
  },

  neon: {
    id: "neon",
    label: "ネオン",
    description: "サイバーパンク風のグロー",
    className: `
      rounded-xl bg-gray-900 border-2 border-cyan-500/50
      shadow-[0_0_20px_rgba(6,182,212,0.3)]
      hover:shadow-[0_0_30px_rgba(6,182,212,0.5)]
      transition-all duration-300
    `.replace(/\s+/g, ' ').trim(),
  },
  */
} as const;

/**
 * スタイルIDの型定義（自動生成）
 * 新しいスタイルを追加すると自動的に型に反映されます
 *
 * @example
 * type ExampleId = CardStyleId; // "glossy" | "standard"
 */
export type CardStyleId = keyof typeof CARD_STYLES;

/**
 * スタイル設定オブジェクトの型定義
 *
 * @example
 * const config: CardStyleConfig = CARD_STYLES.standard;
 */
export type CardStyleConfig = (typeof CARD_STYLES)[CardStyleId];

/**
 * デフォルトスタイルID
 * Flat（シンプルなボーダースタイル）をデフォルトとする
 */
export const DEFAULT_CARD_STYLE: CardStyleId = "standard";
